import { useState } from "react";
import { useTranslation } from "react-i18next";
// TODO: dropdown left to clean up
import { Dropdown, Form } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan, useSelectedLocation } from "components";
import { FIXED_VIN_LENGTH } from "modules/Appointments/components/NewAppointment/utils";
import { useAddPinPendingChecklistMutation } from "modules/PendingChecklistsDashboard/components/PinChecklists/components/PinChecklistModal/components/AddPinPendingChecklist/hooks";
import { useChecklistDropdownOptions } from "modules/PendingChecklistsDashboard/hooks";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { Checklist, PIN_TYPES } from "models";
import type { SortablePinPendingChecklists } from "modules/PendingChecklistsDashboard/components/PinChecklists/components/PinChecklistModal";
import type { GroupedChecklistsDropdownOptions } from "modules/PendingChecklistsDashboard/hooks";

type AddPinPendingChecklistProps = {
  items: SortablePinPendingChecklists[];
  pinType: PIN_TYPES | null;
  nextOrdering: number;
  checklists?: Checklist[];
};

export const AddPinPendingChecklist = ({ nextOrdering, pinType, items, checklists }: AddPinPendingChecklistProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { mutate: addPinPendingChecklist } = useAddPinPendingChecklistMutation();
  const canAddPendingChecklist = useCan("create", "pin-pending-checklist");
  const { groupedChecklistsDropdownOptions } = useChecklistDropdownOptions(checklists);

  const [vinPrefix, setVinPrefix] = useState("");
  const [selectedChecklistId, setSelectedChecklistId] = useState(0);

  const excludeSelectedChecklistDropdownOptions = groupedChecklistsDropdownOptions.reduce<GroupedChecklistsDropdownOptions[]>((acc, cdo) => {
    if (!items.some(i => i.checklist_id === cdo.key)) acc.push(cdo);
    return acc;
  }, []);

  const handleAddItem = () => {
    if (selectedChecklistId) {
      const selectedChecklist = checklists?.find(cl => cl.id === selectedChecklistId);

      if (selectedChecklist && selectedLocation && pinType) {
        setSelectedChecklistId(0);
        setVinPrefix("");
        addPinPendingChecklist({
          pin_type: pinType,
          ordering: nextOrdering,
          vin_prefix: vinPrefix,
          checklist_id: selectedChecklist.id,
          checklist_name: selectedChecklist.name,
          dealer_location_id: selectedLocation.id,
          created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
          updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)
        });
      }
    }
  };

  return (
    <Form>
      <div className="add-checklists-container">
        <div className="full-width">
          <Dropdown
            fluid
            selection
            clearable
            search
            selectOnBlur={false}
            disabled={!canAddPendingChecklist}
            options={excludeSelectedChecklistDropdownOptions}
            value={selectedChecklistId > 0 ? selectedChecklistId : ""}
            placeholder={t("v8_select_mandatory_checklists", "Select mandatory checklists")}
            onChange={(_e, { value }) => setSelectedChecklistId(Number(value) || 0)}
          />
        </div>
        <div className="full-width">
          <Form.Field error={vinPrefix.length > FIXED_VIN_LENGTH}>
            <Form.Input fluid value={vinPrefix} placeholder={`${t("v8_enter_vin", "Enter VIN")} #`} onChange={(_e, { value }) => setVinPrefix(value)} />
          </Form.Field>
        </div>
        <Form.Button
          type="button"
          color="green"
          className="add-button"
          onClick={handleAddItem}
          disabled={!canAddPendingChecklist || !selectedChecklistId || vinPrefix.length > FIXED_VIN_LENGTH}
        >
          {t("v8_add", "Add")}
        </Form.Button>
      </div>
    </Form>
  );
};
